import { IBookingPurchaseOptionItem } from "@/api_framework/api_modals/Bookings";
import { toast } from "react-hot-toast";
import { MinusIcon,PlusIcon } from "@heroicons/react/24/outline";
import smart from "@/assets/smart.svg";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";

interface IMembershipPlanCardProps {
  isLoading: boolean;
  membershipOption: IBookingPurchaseOptionItem|null;
  membershipBillingStatus?: string|null;
  membershipPerSessionLabel: string;
  membershipPrice: number;
  membershipOriginalPrice: number;
  isSelected: boolean;
  formatAmount: (amount: number) => string;
  onAdd: (membershipId: string,membershipName: string) => void;
  onRemove: () => void;
  selectedCouponId?: string;
  onSelectCoupon?: (couponId: string | null | undefined, couponName: string | null | undefined) => void;
}

const skeletonClass="animate-pulse rounded bg-[#E9ECF2]";

const MembershipPlanCard: React.FC<IMembershipPlanCardProps>=({
  isLoading,
  membershipOption,
  membershipBillingStatus,
  membershipPerSessionLabel,
  membershipPrice,
  membershipOriginalPrice,
  isSelected,
  formatAmount,
  onAdd,
  onRemove,
  selectedCouponId,
  onSelectCoupon,
}) => {
  if (isLoading) {
    return (
      <div
        className="mt-4 rounded-2xl border border-[#E2E4EA] px-4 py-4"
        style={{
          background: "linear-gradient(111.32deg, #FFFFFF 29.47%, rgba(255, 164, 164, 0.3) 106.2%)",
        }}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full space-y-2">
            <div className={`${skeletonClass} h-5 w-3/4`} />
            <div className={`${skeletonClass} h-4 w-1/2`} />
            <div className={`${skeletonClass} h-3 w-2/3`} />
          </div>
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <div className={`${skeletonClass} h-5 w-20`} />
              <div className={`${skeletonClass} h-4 w-16`} />
            </div>
            <div className={`${skeletonClass} h-10 w-[84px] rounded-xl`} />
          </div>
        </div>
      </div>
    );
  }

  if (!membershipOption) {
    return null;
  }

  const quantityAdded=Number(membershipOption.qty??(isSelected? 1:0));
  const maxQuantity=Number(membershipOption.max_qty??1);
  const isMaxQtyReached=quantityAdded>=maxQuantity;
  const isBilledMembership=isSelected&&String(membershipBillingStatus??"").toUpperCase()==="BILLED";
  const membershipDiscountPercent=
    membershipOriginalPrice>0
      ? Math.max(0,Math.round(((membershipOriginalPrice-membershipPrice)/membershipOriginalPrice)*100))
      :0;
  const hasMembershipDiscount=membershipDiscountPercent>0&&membershipOriginalPrice>membershipPrice;

  const coupons=membershipOption.applicable_coupons||[];
  const couponItems=coupons.map((c) => ({
    id: c.coupon_id,
    title: c.coupon_name,
    state: { coupon_visibility: c.coupon_visibility },
  }));
  const defaultSelectedCoupon=selectedCouponId
    ? couponItems.find((item) => item.id===selectedCouponId)
    :undefined;

  return (
    <div
      className={`mt-4 rounded-[10px] px-[12px] py-[12px] ${isSelected? "border-2 border-[#00873C]":"border border-[#E2E2E2]"}`}
      style={{
        background: isSelected
          ? "radial-gradient(ellipse at 50% 0%, rgba(164, 255, 181, 0.45) 0%, rgba(164, 255, 181, 0) 72%), linear-gradient(111.32deg, #FFFFFF 29.47%, rgba(164, 255, 181, 0.3) 106.2%)"
          :"linear-gradient(111.32deg, #FFFFFF 29.47%, rgba(255, 164, 164, 0.3) 106.2%)",
      }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:justify-between">
        <div className="flex-1">
          <p className="text-[14px] font-medium text-[#222833]">{membershipOption.name}</p>
          <div className="mt-2 flex items-center gap-2">
            <p className="text-[12px] font-medium text-[#6D6D6D]">{membershipPerSessionLabel}</p>
            {isSelected&&(
              <span
                className="px-2 py-0.5 text-xs font-medium flex gap-2"
                style={{
                  background: "linear-gradient(111.32deg, #EAFFE6 29.47%, #C7FFD2 106.2%)",
                  borderRadius: "44px",
                }}
              >
                <img src={smart} alt="Smart Choice" className="h-3.5 w-3.5" />
                <p className="text-[#079A27]">  Smart Choice</p>

              </span>
            )}
          </div>
          {membershipOption.details&&(
            <ul className="mt-3 space-y-1 text-[12px] font-medium text-[#016516]">
              <li>{`\u2022 ${membershipOption.details}`}</li>
            </ul>
          )}
        </div>
        <div className="flex w-full items-end justify-between gap-4 sm:w-auto sm:self-stretch sm:flex-col sm:items-end sm:justify-between">
          <div className="text-right">
            <p className="text-sm font-bold text-[#1F2431]">{formatAmount(membershipPrice)}</p>
            {hasMembershipDiscount&&(
              <p className="text-sm text-[#8B92A1] line-through">{formatAmount(membershipOriginalPrice)}</p>
            )}
          </div>
          {isSelected? (
            <div className="flex items-center justify-end gap-2 w-full mt-2">
              <SearchableSelectMenu
                errorMessages={[]}
                fieldError={undefined}
                register={{} as any}
                selectItems={couponItems}
                onSelectItem={(item) => {
                  if (item) {
                    onSelectCoupon?.(item.id,item.title);
                  } else {
                    onSelectCoupon?.(null,null);
                  }
                }}
                filterOnEmptyQuery={(item) => item.state?.coupon_visibility!==false}
                onEnterPress={(typedValue) => {
                  if (!typedValue) return;
                  const exactMatch=coupons.find(
                    (c) =>  c.coupon_name?.toLowerCase()===typedValue.toLowerCase(),
                  );
                  if (exactMatch) {
                    if (exactMatch.coupon_id===selectedCouponId) return;
                    onSelectCoupon?.(exactMatch.coupon_id,exactMatch.coupon_name);
                  } else {
                    toast.error("Coupon not applicable");
                  }
                }}
                placeholder={coupons.length===0? "No Applicable Coupons":"Select Coupon"}
                disabled={coupons.length===0}
                defaultSelected={defaultSelectedCoupon}
                showDropdownIcon={true}
                withReset={true}
                listZIndex="z-50"
                className="text-[12px] font-medium text-[#3B4250] h-full py-0"
                inputContainerClassName="h-[35px] border-[#D8DBE2] rounded-[6px] bg-white text-[12px] flex items-center"
                containerClassName="w-[240px] min-w-[250px]"
              />

              {isBilledMembership? (
                <div className="inline-flex h-[30px] min-w-[74px] items-center justify-center rounded-[6px] bg-white px-4 text-[12px] font-medium text-[#005303]">
                  Billed
                </div>
              ):(
                <div className="flex h-[30px] max-w-[74px] items-center rounded-[6px] bg-white px-1">
                  <button type="button" className="p-1 text-[#2A3140]" onClick={onRemove}>
                    <MinusIcon className="h-4 w-4" />
                  </button>
                  <span className="px-2 text-sm text-[#222222]">{quantityAdded}</span>
                  <button
                    type="button"
                    className={`p-1 ${isMaxQtyReached? "cursor-not-allowed text-[#8F8F8F]":"text-[#2A3140]"}`}
                    disabled={isMaxQtyReached}
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ):(
            <button
              type="button"
              onClick={() => onAdd(membershipOption.id,membershipOption.name)}
              className="h-[30px] min-w-[74px] rounded-[6px]  bg-white px-4 text-[12px] text-[#242933]"
            >
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipPlanCard;
